import { ComponentProps, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import Loading from 'modules/core/components/Loading'

import { F0Alert, F0Box, F0Dialog } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'

import {
  PublishSurveyParticipantsEntitiesDocument,
  PublishSurveyPreviewDocument,
  PublishSurveyWithCurrentScheduleSettingsDocument,
} from 'generated/resources/graphql'
import capitalize from 'lodash/capitalize'
import { i18nDateToString } from 'modules/core/lib/dates'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formRecurrenceMapper } from 'modules/engagement_surveys/lib/formRecurrenceMapper'
import { scheduleStartDateFromUTC } from 'modules/engagement_surveys/lib/getScheduleStartDate'
import { getParticipantsLabel } from 'modules/engagement_surveys/lib/participantStrategyLabels'

type Data = {
  surveyId: string
}

export const PublishSurveyActivity: ActivityComponent<Data> = ({ surveyId }) => {
  const { open, closeActivity } = useActivity()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data, loading } = useQuery(PublishSurveyPreviewDocument, {
    variables: { surveyId: Number(surveyId) },
    skip: !surveyId,
    fetchPolicy: 'cache-and-network',
  })

  const survey = data?.surveys.survey
  const strategy = survey?.respondentStrategy?.strategy
  const strategyArguments = survey?.respondentStrategy?.arguments

  const { data: participantsData } = useQuery(PublishSurveyParticipantsEntitiesDocument, {
    variables: {
      ids: strategyArguments,
      getTeams: strategy === 'teams',
      getLocations: strategy === 'locations',
      getLegalEntities: strategy === 'legal_entities',
      getEmployees: strategy === 'employees',
    },
    skip:
      !strategy ||
      !['teams', 'locations', 'legal_entities', 'employees'].includes(strategy) ||
      !strategyArguments?.length,
  })

  const [publishSurvey] = useMutation(PublishSurveyWithCurrentScheduleSettingsDocument, {
    extractorFn: (mutationData) =>
      mutationData.surveysMutations.publishSurveyWithCurrentScheduleSettingsSurveysSurvey,
  })

  const surveyForm = survey?.form
  const schedule = surveyForm?.schedule

  const participantsAvatarList = useMemo(() => {
    if (strategy === 'teams') {
      const connection = participantsData?.teams?.teamsConnection
      if (!connection?.nodes?.length) return undefined
      const avatars = connection.nodes.map((t) => ({
        name: t.name,
        src: t.avatar ?? undefined,
      }))
      const totalCount = connection.totalCount ?? avatars.length
      let remainingCount: number | undefined = Math.max(totalCount - avatars.length, 0)
      remainingCount = remainingCount === 0 ? undefined : remainingCount
      return { avatars, size: 'sm' as const, remainingCount, type: 'team' as const }
    }
    if (strategy === 'locations') {
      const connection = participantsData?.locations?.locationsConnection
      if (!connection?.nodes?.length) return undefined
      const avatars = connection.nodes.map((l) => ({
        name: l.name,
      }))
      const totalCount = connection.totalCount ?? avatars.length
      let remainingCount: number | undefined = Math.max(totalCount - avatars.length, 0)
      remainingCount = remainingCount === 0 ? undefined : remainingCount
      return { avatars, size: 'sm' as const, remainingCount, type: 'team' as const }
    }
    if (strategy === 'legal_entities') {
      const connection = participantsData?.companies?.legalEntitiesConnection
      if (!connection?.nodes?.length) return undefined
      const avatars = connection.nodes.map((le) => ({
        name: le.legalName,
      }))
      const totalCount = connection.totalCount ?? avatars.length
      let remainingCount: number | undefined = Math.max(totalCount - avatars.length, 0)
      remainingCount = remainingCount === 0 ? undefined : remainingCount
      return { avatars, size: 'sm' as const, remainingCount, type: 'company' as const }
    }
    if (strategy === 'employees') {
      const connection = participantsData?.employees?.employeeNamesConnection
      if (!connection?.nodes?.length) return undefined
      const avatars = connection.nodes.map((e) => ({
        firstName: e.preferredName ?? e.firstName,
        lastName: e.lastName,
        src: e.avatar?.url,
      }))
      const totalCount = connection.totalCount ?? avatars.length
      let remainingCount: number | undefined = Math.max(totalCount - avatars.length, 0)
      remainingCount = remainingCount === 0 ? undefined : remainingCount
      return { avatars, size: 'sm' as const, remainingCount, type: 'person' as const }
    }
    return undefined
  }, [strategy, participantsData])

  const startDate = useMemo(() => {
    if (schedule?.startDate) {
      return scheduleStartDateFromUTC(schedule.startDate)
    }
    return null
  }, [schedule?.startDate])

  const hasStartDate = startDate !== null
  const hasQuestions = (surveyForm?.elementsConnection?.totalCount ?? 0) > 0

  const details: ComponentProps<typeof DetailsItemsList>['details'] = useMemo(() => {
    if (!survey) return []

    const publishOnText = startDate
      ? i18nDateToString(startDate, 'ui_date_with_time')
      : i18n.t('surveys.publish_dialog.now')

    const recurrenceType = schedule?.recurrenceType ?? 'onetime'

    const participantsContent = participantsAvatarList
      ? {
          avatarList: participantsAvatarList,
          type: 'avatar-list' as const,
        }
      : { text: getParticipantsLabel(strategy), type: 'item' as const }

    return [
      {
        title: i18n.t('surveys.publish_dialog.participants'),
        content: participantsContent,
      },
      {
        title: i18n.t('surveys.publish_dialog.publish_on'),
        content: {
          text: publishOnText,
          type: 'item' as const,
        },
      },
      {
        title: i18n.t('forms.schedule.recurrence'),
        content: {
          text: formRecurrenceMapper(recurrenceType),
          type: 'item' as const,
        },
      },
      {
        title: i18n.t('surveys.configuration.privacy_settings.anonymous_answers'),
        content: {
          text: capitalize(surveyForm?.anonymous ? i18n.t('common.yes') : i18n.t('common.no')),
          type: 'item' as const,
        },
      },
    ]
  }, [survey, surveyForm, schedule, startDate, strategy, participantsAvatarList])

  const handlePublish = async () => {
    const result = await publishSurvey({
      variables: {
        surveyId: Number(surveyId),
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('surveys.publish_dialog.success_message'),
        time: 1000,
      })
      closeActivity()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('surveys.publish_dialog.success_message'),
        time: 1000,
      })
    }
  }

  const handleEditSettings = () => {
    closeActivity()
    navigate(
      navigation.engagement.smart_surveys.survey.settings.fullPath({
        surveyId: Number(surveyId),
      })
    )
  }

  return (
    <F0Dialog
      isOpen={open}
      onClose={closeActivity}
      title={i18n.t('surveys.publish_dialog.title')}
      description={i18n.t('surveys.publish_dialog.description')}
      primaryAction={{
        label: hasStartDate
          ? i18n.t('surveys.publish_dialog.schedule')
          : i18n.t('surveys.publish_dialog.publish'),
        onClick: handlePublish,
        disabled: !hasQuestions,
      }}
      secondaryAction={{
        label: i18n.t('surveys.publish_dialog.edit_settings'),
        onClick: handleEditSettings,
      }}
    >
      {loading ? (
        <F0Box padding="xl">
          <Loading />
        </F0Box>
      ) : (
        <F0Box display="flex" flexDirection="column" gap="xl">
          <DetailsItemsList details={details} tableView />
          {!hasQuestions && (
            <F0Alert
              variant="warning"
              title={i18n.t('surveys.publish_dialog.errors.no_elements')}
              description={i18n.t('surveys.publish_dialog.errors.no_elements_description')}
            />
          )}
        </F0Box>
      )}
    </F0Dialog>
  )
}
